import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, Edit, CheckCircle, Download, Mail, Receipt, BellRing, RefreshCcw, Copy, Send } from 'lucide-react';
import { formatDate, caymanToday } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';
import { formatDiscountBadge } from '../../utils/discountMath';
import { IssueRefundModal } from '../../components/admin/IssueRefundModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ViewInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  
  const [invoice, setInvoice] = useState(null);
  const [existingReceipt, setExistingReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [sending, setSending] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [issuingReceipt, setIssuingReceipt] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchInvoice();
    fetchExistingReceipt();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${id}`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(data);
      } else if (response.status === 401) {
        navigate('/admin/login');
      } else {
        alert('Invoice not found');
        navigate('/admin/invoices');
      }
    } catch (err) {
      logger.error('Failed to fetch invoice:', err);
      alert('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingReceipt = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
      });
      if (response.ok) {
        const receipts = await response.json();
        const match = receipts.find(r => r.invoice_id === id);
        setExistingReceipt(match || null);
      }
    } catch (err) {
      logger.error('Failed to fetch receipts:', err);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!window.confirm('Mark this invoice as paid?')) return;
    
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`
        },
        body: JSON.stringify({
          status: 'paid',
          payment_date: caymanToday(),
          payment_method: 'bank_transfer'
        })
      });

      if (response.ok) {
        fetchInvoice();
        alert('Invoice marked as paid!');
      }
    } catch (err) {
      alert('Failed to update invoice');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchInvoice();
      }
    } catch (err) {
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendEmail = async () => {
    if (!window.confirm(`Send invoice to ${invoice.client_email}?`)) return;
    
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${id}/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        alert(`Invoice sent successfully to ${invoice.client_email}!`);
      } else {
        const error = await response.json();
        alert(`Failed to send email: ${error.detail}`);
      }
    } catch (err) {
      alert('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleSendReminder = async () => {
    if (!window.confirm(
      `Send a payment reminder to ${invoice.client_name} (${invoice.client_email})?\n\n` +
      `Invoice ${invoice.invoice_number} — ${invoice.currency}${(invoice.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    )) return;
    setReminderSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${id}/send-payment-reminder`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Payment reminder sent to ${invoice.client_email}.\nOutstanding balance: ${invoice.currency}${(data.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed to send reminder: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      logger.error('Send invoice reminder error:', err);
      alert('Failed to send reminder. Please try again.');
    } finally {
      setReminderSending(false);
    }
  };

  const handleDuplicate = async () => {
    if (!window.confirm('Duplicate this invoice? A new pending invoice with the same line items and discounts will be created (client info will be blank).')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (response.ok) {
        const inv = await response.json();
        navigate(`/admin/invoices/${inv.id}/edit`);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed to duplicate: ${err.detail || response.status}`);
      }
    } catch (err) {
      logger.error('Duplicate invoice failed:', err);
      alert('Network error. Please try again.');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${id}/download-pdf`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${invoice.client_name.replace(/\s/g, '_')}_${invoice.invoice_number.replace(/\//g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (err) {
      logger.error('Download error:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handleIssueReceipt = async () => {
    const method = window.prompt(
      'Payment method? Type one of: bank_transfer, wire, cash, card',
      invoice.payment_method || 'bank_transfer'
    );
    if (!method) return;

    const sendEmail = window.confirm(
      'Email the receipt PDF to the client as well?\n\nOK = Yes, send email\nCancel = Just create receipt, no email'
    );

    setIssuingReceipt(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/invoices/${id}/generate-receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`
        },
        body: JSON.stringify({
          payment_method: method,
          send_email: sendEmail,
        })
      });

      const data = await response.json();
      if (response.ok) {
        if (data.already_existed) {
          if (window.confirm(`${data.message}\n\nView the existing receipt?`)) {
            navigate(`/admin/receipts/${data.receipt_id}`);
          }
        } else {
          alert(data.message);
          fetchInvoice();
          if (window.confirm('View the new receipt?')) {
            navigate(`/admin/receipts/${data.receipt_id}`);
          }
        }
      } else {
        alert(`Failed to issue receipt: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      logger.error('Issue receipt error:', err);
      alert('Failed to issue receipt. Please try again.');
    } finally {
      setIssuingReceipt(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      paid: 'bg-green-100 text-green-800 border-green-200',
      overdue: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return (
      <span className={`px-4 py-2 rounded-lg text-sm font-semibold border ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground/60">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoice) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between admin-header-row">
            <div>
              <h1 className="text-2xl font-bold text-primary">Invoice {invoice.invoice_number}</h1>
              <p className="text-sm text-foreground/60">View invoice details</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/admin/invoices')}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={handleDownloadPDF}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
              <button
                onClick={() => navigate(`/admin/invoices/${id}/edit`)}
                className="px-4 py-2 text-sm border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={handleDuplicate}
                data-testid="duplicate-invoice-btn"
                title="Create a new pending invoice with the same line items and discounts"
                className="px-4 py-2 text-sm border border-border text-foreground rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Duplicate
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sending}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Mail className="w-4 h-4" />
                {sending ? 'Sending...' : 'Email Invoice'}
              </button>
              <button
                onClick={() => navigate(`/admin/email-composer?attach=invoice:${id}&to=${encodeURIComponent(invoice.client_email || '')}&subject=${encodeURIComponent(`Invoice ${invoice.invoice_number} from Pawsome Ventures`)}`)}
                data-testid="compose-with-invoice-btn"
                title="Open the composer with this invoice pre-attached (add other documents, CC teammates, upload photos, etc.)"
                className="px-4 py-2 text-sm border-2 border-blue-500 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Compose…
              </button>
              {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                <button
                  onClick={handleSendReminder}
                  disabled={reminderSending}
                  data-testid="send-invoice-reminder-btn"
                  className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                  title="Email a payment reminder with the invoice attached"
                >
                  <BellRing className={`w-4 h-4 ${reminderSending ? 'animate-pulse' : ''}`} />
                  {reminderSending ? 'Sending...' : 'Send Reminder'}
                </button>
              )}
              {invoice.status === 'pending' && (
                <button
                  onClick={handleMarkAsPaid}
                  disabled={updating}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  data-testid="mark-as-paid-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark as Paid
                </button>
              )}
              <button
                onClick={existingReceipt ? () => navigate(`/admin/receipts/${existingReceipt.id}`) : handleIssueReceipt}
                disabled={issuingReceipt}
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="issue-receipt-btn"
                title={existingReceipt ? `View existing receipt ${existingReceipt.receipt_number}` : 'Generate a payment receipt for this invoice'}
              >
                <Receipt className="w-4 h-4" />
                {issuingReceipt ? 'Issuing…' : existingReceipt ? `View Receipt ${existingReceipt.receipt_number}` : 'Issue Receipt'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Invoice Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Invoice Header */}
            <div className="bg-white rounded-xl p-8 border border-border shadow-sm">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start gap-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_pet-relocation-hub/artifacts/87vqpckq_Pawsome%20Logo.jpg" 
                    alt="Pawsome Ventures" 
                    className="w-24 h-24 rounded-full"
                  />
                  <div>
                    <h2 className="text-3xl font-bold text-foreground mb-2">Pawsome Ventures</h2>
                    <p className="text-foreground/70 text-sm">Pet Travel & Relocation Services</p>
                    <p className="text-foreground/70 text-sm">West Bay, Grand Cayman KY1-1301</p>
                    <p className="text-foreground/70 text-sm">Cayman Islands</p>
                    <p className="text-foreground/70 text-sm mt-2">Tel: 1 345 916 2281</p>
                    <p className="text-foreground/70 text-sm">Email: pawsome.ky@gmail.com</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-foreground/60 mb-1">Date</div>
                  <div className="font-semibold text-foreground mb-3">{formatDate(invoice.invoice_date)}</div>
                  <div className="text-sm text-foreground/60 mb-1">Invoice #</div>
                  <div className="text-xl font-bold text-foreground font-mono mb-3">{invoice.invoice_number}</div>
                  <div className="text-sm text-foreground/60 mb-1">Client</div>
                  <div className="font-semibold text-foreground mb-3">{invoice.client_name}</div>
                  <div>{getStatusBadge(invoice.status)}</div>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-xl overflow-hidden border border-border shadow-sm">
              <table className="w-full">
                <thead className="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-bold text-foreground">Item</th>
                    <th className="px-6 py-3 text-left text-sm font-bold text-foreground">Service & Inventory</th>
                    <th className="px-6 py-3 text-center text-sm font-bold text-foreground">Quantity</th>
                    <th className="px-6 py-3 text-right text-sm font-bold text-foreground">Cost (KYD)</th>
                    <th className="px-6 py-3 text-right text-sm font-bold text-foreground">Total (KYD)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invoice.items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-foreground">{idx + 1}</td>
                      <td className="px-6 py-4 text-foreground">
                        <div>{item.description}</div>
                        {item.discount_type && item.discount_value > 0 && (
                          <div className="text-xs text-red-600 mt-0.5" data-testid={`invoice-item-discount-badge-${idx}`}>
                            {formatDiscountBadge(item, invoice.currency)}{item.discount_label ? ` — ${item.discount_label}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-foreground">{item.quantity}</td>
                      <td className="px-6 py-4 text-right text-foreground">{item.unit_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-6 py-4 text-right font-semibold text-foreground">{item.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-gray-300">
                  {invoice.discount_type && (invoice.discount_amount || 0) > 0 && (
                    <>
                      <tr>
                        <td colSpan="4" className="px-6 py-2 text-right text-sm text-foreground/70">Subtotal:</td>
                        <td className="px-6 py-2 text-right text-sm font-semibold text-foreground" data-testid="invoice-subtotal">
                          {invoice.currency}{(invoice.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                      <tr data-testid="invoice-doc-discount-row">
                        <td colSpan="4" className="px-6 py-2 text-right text-sm text-red-700">
                          Discount{invoice.discount_type === 'percent' ? ` (${invoice.discount_value}%)` : ''}{invoice.discount_label ? ` — ${invoice.discount_label}` : ''}:
                        </td>
                        <td className="px-6 py-2 text-right text-sm font-semibold text-red-700">
                          −{invoice.currency}{(invoice.discount_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-right text-lg font-bold text-foreground">TOTAL:</td>
                    <td className="px-6 py-4 text-right text-lg font-bold text-foreground">{invoice.currency}{invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
              
              {/* Payment Terms */}
              <div className="px-6 py-4 bg-gray-50 border-t border-border">
                <p className="text-sm font-semibold text-foreground">{invoice.payment_terms || 'Payment is due in full at the time of invoicing.'}</p>
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
              <h3 className="text-lg font-bold text-foreground mb-4">Payment Information</h3>
              
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-3 gap-4">
                  <div className="font-semibold text-foreground">Bank:</div>
                  <div className="col-span-2 text-foreground">{invoice.bank_name || 'Cayman National Bank'}</div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="font-semibold text-foreground">Account Holder:</div>
                  <div className="col-span-2 text-foreground">{invoice.account_holder || 'Samantha Dorman T/A Pawsome Ventures'}</div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="font-semibold text-foreground">Account Type:</div>
                  <div className="col-span-2 text-foreground">{invoice.account_type || 'CI Savings'}</div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="font-semibold text-foreground">Account Number:</div>
                  <div className="col-span-2 text-foreground">{invoice.account_number || '012-86563'}</div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="font-semibold text-foreground">Reference:</div>
                  <div className="col-span-2 text-foreground font-semibold">{invoice.payment_reference || invoice.client_name.toUpperCase().split(' ')[0]}</div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-xs text-foreground/60 italic">
                  For wire transfers (USD), three correspondent bank options are available (Citibank, Wells Fargo, BNY Mellon). Please contact us for complete wire transfer details.
                </p>
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
                <h3 className="text-lg font-bold text-foreground mb-3">Notes</h3>
                <p className="text-foreground/70 whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 border border-border shadow-sm sticky top-6 space-y-4">
              <h3 className="text-lg font-bold text-foreground mb-4">Actions</h3>
              
              <button
                onClick={handleDownloadPDF}
                className="w-full px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </button>

              <button
                onClick={() => navigate(`/admin/invoices/${id}/edit`)}
                className="w-full px-4 py-3 border-2 border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
              >
                <Edit className="w-5 h-5" />
                Edit Invoice
              </button>

              <button
                onClick={handleSendEmail}
                disabled={sending}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Mail className="w-5 h-5" />
                {sending ? 'Sending Email...' : 'Send to Client'}
              </button>

              {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                <button
                  onClick={handleSendReminder}
                  disabled={reminderSending}
                  data-testid="send-invoice-reminder-sidebar-btn"
                  className="w-full px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  title="Email a payment reminder with the invoice PDF attached"
                >
                  <BellRing className={`w-5 h-5 ${reminderSending ? 'animate-pulse' : ''}`} />
                  {reminderSending ? 'Sending Reminder...' : 'Send Payment Reminder'}
                </button>
              )}

              {invoice.status === 'pending' && (
                <button
                  onClick={handleMarkAsPaid}
                  disabled={updating}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <CheckCircle className="w-5 h-5" />
                  Mark as Paid
                </button>
              )}

              {invoice.status === 'paid' && (
                <button
                  onClick={() => setRefundOpen(true)}
                  data-testid="open-refund-from-invoice-btn"
                  className="w-full px-4 py-3 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                  title="Issue a full or partial refund against this paid invoice"
                >
                  <RefreshCcw className="w-5 h-5" />
                  Issue Refund
                </button>
              )}

              <div className="pt-4 border-t border-border">
                <h4 className="text-sm font-semibold text-foreground/60 mb-3">Change Status</h4>
                <div className="space-y-2">
                  {['pending', 'paid', 'overdue', 'cancelled'].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleUpdateStatus(status)}
                      disabled={invoice.status === status || updating}
                      className="w-full px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-border text-sm text-foreground/60">
                <div className="flex justify-between mb-2">
                  <span>Created:</span>
                  <span>{formatDate(invoice.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Last Updated:</span>
                  <span>{formatDate(invoice.updated_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <IssueRefundModal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        sourceType="invoice"
        sourceId={invoice.id}
        sourceNumber={invoice.invoice_number}
        paidAmount={parseFloat(invoice.total) || 0}
        currency={invoice.currency || 'CI$'}
        clientName={invoice.client_name}
        onIssued={() => alert(`Refund issued and emailed to ${invoice.client_email}.`)}
      />
    </div>
  );
};
